import React from 'react';
import { useNavigate } from 'react-router-dom';
import LandingHeader from '../components/organisms/LandingHeader';
import Footer from '../components/organisms/Footer';
import { FileText, Shield, Users, AlertCircle, Scale, Lock } from 'lucide-react';

const TermsAndConditionsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-gray-50">
      <LandingHeader />
      
      <div className="max-w-5xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="bg-[#0a1929] rounded-2xl p-12 text-white mb-8">
          <div className="flex items-center gap-3 mb-4">
            <FileText className="w-10 h-10" />
            <h1 className="text-4xl font-bold">Terms and Conditions</h1>
          </div>
          <p className="text-lg text-gray-300">Last Updated: March 3, 2026</p>
          <p className="mt-4 text-gray-300">
            Please read these terms and conditions carefully before using Aptirix services.
          </p>
        </div>

        {/* Quick Navigation */}
        <div className="bg-white rounded-xl shadow-md p-6 mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Navigation</h2>
          <div className="grid md:grid-cols-3 gap-4">
            <a href="#acceptance" className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm">
              <Shield className="w-4 h-4" />
              Acceptance of Terms
            </a>
            <a href="#services" className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm">
              <Users className="w-4 h-4" />
              Services Description
            </a>
            <a href="#user-accounts" className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm">
              <Lock className="w-4 h-4" />
              User Accounts
            </a>
            <a href="#privacy" className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm">
              <Shield className="w-4 h-4" />
              Privacy & Data
            </a>
            <a href="#intellectual-property" className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm">
              <Scale className="w-4 h-4" />
              Intellectual Property
            </a>
            <a href="#liability" className="flex items-center gap-2 text-indigo-600 hover:text-indigo-700 text-sm">
              <AlertCircle className="w-4 h-4" />
              Limitation of Liability
            </a>
          </div>
        </div>

        {/* Main Content */}
        <div className="bg-white rounded-xl shadow-md p-8 space-y-8">
          
          {/* Section 1 */}
          <section id="acceptance">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              1. Acceptance of Terms
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p>
                By accessing and using Aptirix's platform and services ("Services"), you accept and agree to be bound by the terms and provisions of this agreement. If you do not agree to these Terms and Conditions, please do not use our Services.
              </p>
              <p>
                These Terms and Conditions apply to all users of the Services, including but not limited to candidates, recruiters, HR professionals, course providers, and educational institutions.
              </p>
            </div>
          </section>

          {/* Section 2 */}
          <section id="services">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-indigo-600" />
              2. Services Description
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p>
                Aptirix provides an AI-powered career development platform that includes:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>AI-powered interview training and practice sessions</li>
                <li>Resume enhancement and optimization tools</li>
                <li>Job matching and application services</li>
                <li>Online courses and educational content</li>
                <li>Placement training programs</li>
                <li>Recruitment and candidate assessment tools for employers</li>
                <li>Live AI teaching and interactive learning experiences</li>
              </ul>
              <p>
                We reserve the right to modify, suspend, or discontinue any part of our Services at any time without prior notice.
              </p>
            </div>
          </section>

          {/* Section 3 */}
          <section id="user-accounts">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-indigo-600" />
              3. User Accounts and Registration
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">3.1 Account Creation</p>
              <p>
                To access certain features of our Services, you must register for an account. You agree to provide accurate, current, and complete information during the registration process and to update such information to keep it accurate, current, and complete.
              </p>
              
              <p className="font-semibold text-gray-900 mt-4">3.2 Account Security</p>
              <p>
                You are responsible for maintaining the confidentiality of your account credentials and for all activities that occur under your account. You agree to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use a strong and unique password</li>
                <li>Not share your account credentials with others</li>
                <li>Notify us immediately of any unauthorized use of your account</li>
                <li>Log out from your account at the end of each session</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">3.3 Account Termination</p>
              <p>
                We reserve the right to suspend or terminate your account if you violate these Terms and Conditions or engage in fraudulent, abusive, or illegal activities.
              </p>
            </div>
          </section>

          {/* Section 4 */}
          <section id="user-conduct">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-indigo-600" />
              4. User Conduct and Prohibited Activities
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p>You agree not to:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Use the Services for any unlawful purpose or in violation of any applicable laws</li>
                <li>Impersonate any person or entity or falsely state or misrepresent your affiliation</li>
                <li>Interfere with or disrupt the Services or servers or networks connected to the Services</li>
                <li>Attempt to gain unauthorized access to any portion of the Services</li>
                <li>Upload or transmit viruses, malware, or any other malicious code</li>
                <li>Collect or harvest any personally identifiable information from the Services</li>
                <li>Use automated systems (bots, scrapers) to access the Services without permission</li>
                <li>Engage in any activity that could damage, disable, or impair the Services</li>
                <li>Share or distribute course content without authorization</li>
                <li>Cheat or manipulate assessment results</li>
              </ul>
            </div>
          </section>

          {/* Section 5 */}
          <section id="privacy">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Shield className="w-6 h-6 text-indigo-600" />
              5. Privacy and Data Protection
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">5.1 Data Collection</p>
              <p>
                We collect and process personal information as described in our Privacy Policy. By using our Services, you consent to such collection and processing.
              </p>
              
              <p className="font-semibold text-gray-900 mt-4">5.2 AI and Recording</p>
              <p>
                Our Services use AI technology and may record audio, video, and screen content during interview sessions and training for the purposes of:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Providing feedback and performance analysis</li>
                <li>Improving our AI algorithms and Services</li>
                <li>Quality assurance and training purposes</li>
              </ul>
              <p>
                You consent to such recording and processing when you use these features.
              </p>

              <p className="font-semibold text-gray-900 mt-4">5.3 Data Security</p>
              <p>
                We implement appropriate technical and organizational measures to protect your personal data. However, no method of transmission over the Internet is 100% secure, and we cannot guarantee absolute security.
              </p>
            </div>
          </section>

          {/* Section 6 */}
          <section id="intellectual-property">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <Scale className="w-6 h-6 text-indigo-600" />
              6. Intellectual Property Rights
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">6.1 Aptirix Content</p>
              <p>
                All content, features, and functionality of the Services, including but not limited to text, graphics, logos, icons, images, audio clips, video clips, data compilations, software, and the compilation thereof (collectively, "Aptirix Content"), are the exclusive property of Aptirix and are protected by copyright, trademark, and other intellectual property laws.
              </p>
              
              <p className="font-semibold text-gray-900 mt-4">6.2 User Content</p>
              <p>
                You retain ownership of any content you submit, post, or display on or through the Services ("User Content"). By submitting User Content, you grant Aptirix a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, adapt, publish, and display such content for the purpose of providing and improving our Services.
              </p>

              <p className="font-semibold text-gray-900 mt-4">6.3 Course Content</p>
              <p>
                Course materials and educational content are licensed to you for personal, non-commercial use only. You may not reproduce, distribute, modify, or create derivative works from course content without explicit permission.
              </p>
            </div>
          </section>

          {/* Section 7 */}
          <section id="payments">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              7. Payments and Subscriptions
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">7.1 Pricing</p>
              <p>
                Certain features of our Services require payment. All prices are displayed in Indian Rupees (INR) unless otherwise stated. We reserve the right to change our pricing at any time.
              </p>
              
              <p className="font-semibold text-gray-900 mt-4">7.2 Payment Processing</p>
              <p>
                Payments are processed through third-party payment processors (such as Razorpay). You agree to provide accurate payment information and authorize us to charge your payment method for all fees incurred.
              </p>

              <p className="font-semibold text-gray-900 mt-4">7.3 Refund Policy</p>
              <p>
                Refunds are handled on a case-by-case basis. Generally:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Course refunds may be available within 7 days of purchase if less than 20% of content has been accessed</li>
                <li>Subscription fees are non-refundable except as required by law</li>
                <li>Assessment and interview session fees are non-refundable once the session has started</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">7.4 Automatic Renewal</p>
              <p>
                Subscription services automatically renew unless you cancel before the renewal date. You will be charged the then-current subscription fee.
              </p>
            </div>
          </section>

          {/* Section 8 */}
          <section id="liability">
            <h2 className="text-2xl font-bold text-gray-900 mb-4 flex items-center gap-2">
              <AlertCircle className="w-6 h-6 text-indigo-600" />
              8. Limitation of Liability
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">8.1 Service Availability</p>
              <p>
                We strive to provide uninterrupted access to our Services, but we do not guarantee that the Services will be available at all times. We are not liable for any interruption, delay, or failure in the Services.
              </p>
              
              <p className="font-semibold text-gray-900 mt-4">8.2 No Guarantee of Results</p>
              <p>
                While our Services are designed to help improve your career prospects and skills, we do not guarantee:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Job placement or employment</li>
                <li>Interview success or job offers</li>
                <li>Specific learning outcomes or skill improvements</li>
                <li>Accuracy of AI-generated feedback or assessments</li>
              </ul>

              <p className="font-semibold text-gray-900 mt-4">8.3 Limitation of Damages</p>
              <p>
                To the maximum extent permitted by law, Aptirix shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits or revenues, whether incurred directly or indirectly, or any loss of data, use, goodwill, or other intangible losses resulting from:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your use or inability to use the Services</li>
                <li>Any unauthorized access to or use of our servers and/or any personal information stored therein</li>
                <li>Any interruption or cessation of transmission to or from the Services</li>
                <li>Any bugs, viruses, or other harmful code that may be transmitted through the Services</li>
                <li>Any errors or omissions in any content or for any loss or damage incurred as a result of your use of any content posted, emailed, transmitted, or otherwise made available through the Services</li>
              </ul>
            </div>
          </section>

          {/* Section 9 */}
          <section id="indemnification">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              9. Indemnification
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p>
                You agree to indemnify, defend, and hold harmless Aptirix, its officers, directors, employees, agents, and affiliates from and against any claims, liabilities, damages, losses, and expenses, including reasonable attorney's fees, arising out of or in any way connected with:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Your access to or use of the Services</li>
                <li>Your violation of these Terms and Conditions</li>
                <li>Your violation of any third-party rights, including intellectual property rights</li>
                <li>Any User Content you submit or transmit through the Services</li>
              </ul>
            </div>
          </section>

          {/* Section 10 */}
          <section id="third-party">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              10. Third-Party Services and Links
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p>
                Our Services may contain links to third-party websites or services that are not owned or controlled by Aptirix. We have no control over and assume no responsibility for the content, privacy policies, or practices of any third-party websites or services.
              </p>
              <p>
                We use third-party services including but not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Payment processors (Razorpay)</li>
                <li>Cloud hosting providers (Google Cloud Platform)</li>
                <li>AI and machine learning services</li>
                <li>Analytics and monitoring tools</li>
              </ul>
              <p>
                Your use of these third-party services is subject to their respective terms and conditions.
              </p>
            </div>
          </section>

          {/* Section 11 */}
          <section id="termination">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              11. Termination
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p>
                We may terminate or suspend your account and access to the Services immediately, without prior notice or liability, for any reason, including if you breach these Terms and Conditions.
              </p>
              <p>
                Upon termination, your right to use the Services will immediately cease. If you wish to terminate your account, you may simply discontinue using the Services or contact us to request account deletion.
              </p>
              <p>
                All provisions of these Terms and Conditions which by their nature should survive termination shall survive, including ownership provisions, warranty disclaimers, indemnity, and limitations of liability.
              </p>
            </div>
          </section>

          {/* Section 12 */}
          <section id="governing-law">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              12. Governing Law and Dispute Resolution
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">12.1 Governing Law</p>
              <p>
                These Terms and Conditions shall be governed by and construed in accordance with the laws of India, without regard to its conflict of law provisions.
              </p>
              
              <p className="font-semibold text-gray-900 mt-4">12.2 Jurisdiction</p>
              <p>
                Any disputes arising out of or relating to these Terms and Conditions or the Services shall be subject to the exclusive jurisdiction of the courts located in Chennai, Tamil Nadu, India.
              </p>

              <p className="font-semibold text-gray-900 mt-4">12.3 Dispute Resolution</p>
              <p>
                Before filing any legal action, you agree to attempt to resolve any dispute informally by contacting us at support@aptirix.com. We will attempt to resolve the dispute informally within 30 days.
              </p>
            </div>
          </section>

          {/* Section 13 */}
          <section id="changes">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              13. Changes to Terms and Conditions
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p>
                We reserve the right to modify or replace these Terms and Conditions at any time at our sole discretion. If a revision is material, we will provide at least 30 days' notice prior to any new terms taking effect.
              </p>
              <p>
                What constitutes a material change will be determined at our sole discretion. By continuing to access or use our Services after any revisions become effective, you agree to be bound by the revised terms.
              </p>
              <p>
                We encourage you to review these Terms and Conditions periodically for any changes. The "Last Updated" date at the top of this page indicates when these Terms and Conditions were last revised.
              </p>
            </div>
          </section>

          {/* Section 14 */}
          <section id="miscellaneous">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              14. Miscellaneous
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p className="font-semibold text-gray-900">14.1 Entire Agreement</p>
              <p>
                These Terms and Conditions, together with our Privacy Policy, constitute the entire agreement between you and Aptirix regarding the use of our Services.
              </p>
              
              <p className="font-semibold text-gray-900 mt-4">14.2 Severability</p>
              <p>
                If any provision of these Terms and Conditions is found to be unenforceable or invalid, that provision will be limited or eliminated to the minimum extent necessary so that these Terms and Conditions will otherwise remain in full force and effect.
              </p>

              <p className="font-semibold text-gray-900 mt-4">14.3 Waiver</p>
              <p>
                No waiver of any term of these Terms and Conditions shall be deemed a further or continuing waiver of such term or any other term, and Aptirix's failure to assert any right or provision under these Terms and Conditions shall not constitute a waiver of such right or provision.
              </p>

              <p className="font-semibold text-gray-900 mt-4">14.4 Assignment</p>
              <p>
                You may not assign or transfer these Terms and Conditions or your rights hereunder, in whole or in part, without our prior written consent. We may assign these Terms and Conditions at any time without notice.
              </p>

              <p className="font-semibold text-gray-900 mt-4">14.5 Force Majeure</p>
              <p>
                We shall not be liable for any failure or delay in performance due to circumstances beyond our reasonable control, including acts of God, war, terrorism, riots, embargoes, acts of civil or military authorities, fire, floods, accidents, pandemics, strikes, or shortages of transportation facilities, fuel, energy, labor, or materials.
              </p>
            </div>
          </section>

          {/* Contact Section */}
          <section id="contact" className="bg-indigo-50 rounded-xl p-6">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              15. Contact Information
            </h2>
            <div className="space-y-3 text-gray-700 leading-relaxed">
              <p>
                If you have any questions about these Terms and Conditions, please contact us:
              </p>
              <div className="space-y-2 mt-4">
                <p className="font-semibold text-gray-900">Aptirix</p>
                <p>No 7/14 Gengu Road, Egmore, Chennai 08, Tamil Nadu, India</p>
                <p>Email: <a href="mailto:support@aptirix.com" className="text-indigo-600 hover:text-indigo-700">support@aptirix.com</a></p>
                <p>Phone: +91 9094770827 (Mon-Fri, 10 AM to 7 PM IST)</p>
              </div>
            </div>
          </section>

          {/* Acknowledgment */}
          <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <p className="font-semibold text-gray-900 mb-2">Acknowledgment</p>
                <p className="text-gray-700 text-sm">
                  By using Aptirix's Services, you acknowledge that you have read, understood, and agree to be bound by these Terms and Conditions. If you do not agree to these terms, you must not access or use our Services.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Back to Home Button */}
        <div className="text-center mt-8">
          <button
            onClick={() => navigate('/')}
            className="bg-indigo-600 text-white px-8 py-3 rounded-xl font-semibold hover:bg-indigo-700 transition-colors"
          >
            Back to Home
          </button>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default TermsAndConditionsPage;
